import * as React from 'react';
import { useLocation } from "react-router-dom";
import { Box, TextInput, Group, Button, Textarea, NumberInput, Select, Switch } from '@mantine/core'
import { useForm, isNotEmpty } from "@mantine/form";
import axios from 'axios';
import type { IResponse } from "../../types/api";
import type { ISong } from "./types";
import { showMessage } from "../../lib/notification";
import SearchableSelect from "../../components/common/SearchableSelect";
import { FileWithPath, IMAGE_MIME_TYPE } from "@mantine/dropzone";
import FileUpload from "../../components/file-upload";
import { IconChevronDown } from "@tabler/icons-react";
import { vocalItems, licenseItems, phonograms, contractItems, CLIENT_ROUTE, API_ROUTE, CLIENT_TITLE } from "./constants";
import { ISongGroup } from "../song-group/types";
import Page from "../../components/layout/Page";
import { useCategoryNumber } from "../../hooks";
import { assignFiles } from "../../utils/form";

interface FormValues extends Pick<ISong,
    'slug' |
    'code1' |
    'code2' |
    'title' |
    'titleEn' |
    'description' |
    'groupNumber' |
    'lyricBy' |
    'musicBy' |
    'artistName' |
    'lyrics' |
    'vivaNumber' |
    'vocal' |
    'linkFetchedCount' |
    'playedCount' |
    'singerEn' |
    'singer' |
    'youtubeLink' |
    'isFeatured' |
    'featuredOrder' |
    'a100Id' |
    'vivaId' |
    'phonograms' |
    'isClip' |
    'access' |
    'contract' |
    'licenseLyric' |
    'licenseSound' |
    'licenseVideo' |
    'licenseSinger' |
    'isActive'
    > {
    image: string | FileWithPath | null;
    cover: string | FileWithPath | null;
    originalFile: string | FileWithPath | null;
    instrumentFile: string | FileWithPath | null;
    lyricFile: string | FileWithPath | null;
    videoFile: string | FileWithPath | null;
    mLyricFile: string | FileWithPath | null;
    categoryNumber: string;
}

interface IParams extends Omit<FormValues, 'image' | 'cover' | 'videoFile' | 'lyricFile' | 'instrumentFile' | 'originalFile' | 'mLyricFile' | 'categoryNumber'> {
    image?: string | null;
    cover?: string | null;
    videoFile?: string | null;
    lyricFile?: string | null;
    instrumentFile?: string | null;
    originalFile?: string | null;
    mLyricFile?: string | null;
    categoryNumber: number | null;
}

interface IGroupNumberItem extends Pick<ISongGroup, '_id' | 'groupNumber' | 'groupTitle' | 'isActive' | 'isDeleted'> {
}

const EditSong = () => {

    const { state, search } = useLocation();
    const categories = useCategoryNumber();

    const id = React.useMemo(() => {
        if (search && search.startsWith('?')) {
            return search.substring(1);
        }
        return state ?? null;
    }, [state, search])

    const form = useForm<FormValues>({
        initialValues: {
            artistName: "",
            code1: "",
            code2: "",
            description: "",
            groupNumber: 0,
            instrumentFile: "",
            isActive: false,
            lyricBy: "",
            lyricFile: "",
            mLyricFile: "",
            lyrics: "",
            musicBy: "",
            originalFile: "",
            slug: "",
            title: "",
            titleEn: "",
            videoFile: "",
            image: null,
            cover: null,
            linkFetchedCount: 0,
            playedCount: 0,
            vivaNumber: 0,
            vocal: 'none',
            singer: "",
            singerEn: "",
            featuredOrder: 0,
            isFeatured: false,
            youtubeLink: '',
            a100Id: 0,
            access: 0,
            contract: '',
            isClip: false,
            licenseLyric: "",
            licenseSinger: "",
            licenseSound: "",
            licenseVideo: "",
            phonograms: "",
            vivaId: 0,
            categoryNumber: ''
        },

        validate: {
            code1: isNotEmpty('Код-1 оруулна уу'),
            code2: isNotEmpty('Код-2 оруулна уу'),
            title: isNotEmpty('Гарчиг оруулна уу'),
            categoryNumber: isNotEmpty('Ангилал дугаар оруулна уу'),
        },
    });

    React.useEffect(() => {
        const fetchData = async () => {
            const { data } = await axios.get<IResponse<ISong>>(`${API_ROUTE}/${id}`);
            form.setValues({
                ...data.result,
                categoryNumber: data.result.categoryNumber ? data.result.categoryNumber.toString() : ''
            });
            form.resetDirty();
        }
        void fetchData();
    }, []);

    const groupNumberParser = ((groupNumber: IGroupNumberItem) => {
        return {
            value: groupNumber.groupNumber.toString(),
            label: `( ${groupNumber.groupNumber} ) - ${groupNumber.groupTitle}`,
        }
    })

    const handleSubmit = async (values: typeof form.values) => {
        const images = await assignFiles({
            image: values.image,
            cover: values.cover
        }, 'song')
        const files = await assignFiles({
            videoFile: values.videoFile,
            lyricFile: values.lyricFile,
            instrumentFile: values.instrumentFile,
            originalFile: values.originalFile,
            mLyricFile: values.mLyricFile
        }, values.slug)
        const params: IParams = {
            ...values,
            ...images,
            ...files,
            categoryNumber: values.categoryNumber ? Number(values.categoryNumber) : null
        }
        const { data } = await axios.post<IResponse<any>>(`${API_ROUTE}/${id}`, params);
        if (data.statusCode === 200) {
            showMessage('Амжилттай', 'green');
        } else {
            showMessage('Алдаа гарлаа');
        }
    }

    return (
        <Page>
            <Page.Header title={`${CLIENT_TITLE} засах`} backRoute={CLIENT_ROUTE}/>
            <Page.Body>
                <Box sx={{ width: 500 }} mx="auto">
                    <form onSubmit={form.onSubmit(handleSubmit)}>
                        <TextInput
                            withAsterisk
                            label="Код-1"
                            placeholder="Код-1 оруулна уу"
                            {...form.getInputProps('code1')}
                        />
                        <TextInput
                            withAsterisk
                            label="Код-2"
                            placeholder="Код-2 оруулна уу"
                            {...form.getInputProps('code2')}
                        />

                        <TextInput
                            withAsterisk
                            label="Дууны нэр"
                            placeholder="Дууны нэр оруулна уу"
                            {...form.getInputProps('title')}
                        />

                        <TextInput
                            label="Нэр EN"
                            placeholder="Нэр EN оруулна уу"
                            {...form.getInputProps('titleEn')}
                        />

                        <TextInput
                            label="Дуучин"
                            placeholder="Дуучин оруулна уу"
                            {...form.getInputProps('singer')}
                        />

                        <TextInput
                            label="Дуучин EN"
                            placeholder="Дуучин EN оруулна уу"
                            {...form.getInputProps('singerEn')}
                        />

                        <Textarea
                            autosize
                            minRows={2}
                            label="Тайлбар"
                            placeholder="Тайлбар..."
                            {...form.getInputProps('description')}
                        />

                        <FileUpload
                            value={form.values.image}
                            onChange={(value) => {
                                form.setFieldValue('image', value);
                            }}
                            label='Зураг'
                            accept={IMAGE_MIME_TYPE}
                            ratio={'1/1'}
                        />

                        <SearchableSelect<IGroupNumberItem>
                            uri={'/api/song-group/search'}
                            parser={groupNumberParser}
                            selectProps={{
                                label: 'Бүлгийн дугаар',
                                withAsterisk: true,
                                ...form.getInputProps('groupNumber')
                            }}
                        />

                        <Select
                            withAsterisk
                            label='Ангилал дугаар'
                            data={categories}
                            {...form.getInputProps('categoryNumber')}
                        />

                        <NumberInput
                            hideControls
                            label="Viva дугаар"
                            placeholder="Viva дугаар оруулна уу"
                            {...form.getInputProps('vivaNumber')}
                        />

                        <Select
                            label="Вокал"
                            rightSection={<IconChevronDown size={14} />}
                            rightSectionWidth={30}
                            styles={{ rightSection: { pointerEvents: 'none' } }}
                            data={vocalItems}
                            {...form.getInputProps('vocal')}
                        />

                        <TextInput
                            label="Үг бичсэн"
                            placeholder="Үг бичсэн оруулна уу"
                            {...form.getInputProps('lyricBy')}
                        />

                        <TextInput
                            label="Хөгжим"
                            placeholder="Хөгжим оруулна уу"
                            {...form.getInputProps('musicBy')}
                        />

                        <TextInput
                            label="Продюсер"
                            placeholder="Продюсер оруулна уу"
                            {...form.getInputProps('producedBy')}
                        />

                        <Textarea
                            autosize
                            minRows={2}
                            label="Дууны үг"
                            placeholder="Дууны үг..."
                            {...form.getInputProps('lyrics')}
                        />

                        <FileUpload
                            value={form.values.originalFile}
                            onChange={(value) => {
                                form.setFieldValue('originalFile', value);
                            }}
                            label='Дуучинтай файл'
                            type={'file'}
                        />

                        <FileUpload
                            value={form.values.instrumentFile}
                            onChange={(value) => {
                                form.setFieldValue('instrumentFile', value);
                            }}
                            label='Дуучингүй файл'
                            type={'file'}
                        />

                        <FileUpload
                            value={form.values.lyricFile}
                            onChange={(value) => {
                                form.setFieldValue('lyricFile', value);
                            }}
                            label='Үг NC файл'
                            type={'file'}
                        />

                        <FileUpload
                            value={form.values.mLyricFile}
                            onChange={(value) => {
                                form.setFieldValue('mLyricFile', value);
                            }}
                            label='Монгол бичгийн файл'
                            type={'file'}
                        />

                        <NumberInput
                            hideControls
                            disabled
                            label="Холбоос авсан тоо"
                            placeholder="Холбоос авсан тоо"
                            {...form.getInputProps('linkFetchedCount')}
                        />

                        <NumberInput
                            hideControls
                            disabled
                            label="Тоглогдсон тоо"
                            placeholder="Тоглогдсон тоо"
                            {...form.getInputProps('playedCount')}
                        />

                        <TextInput
                            label="YouTube холбоос"
                            placeholder="YouTube холбоос"
                            {...form.getInputProps('youtubeLink')}
                        />

                        <Switch
                            label="Онцолсон эсэх"
                            {...form.getInputProps('isFeatured', { type: 'checkbox' })}
                        />

                        <NumberInput
                            hideControls
                            label="Онцолсон дараалал"
                            placeholder="Онцолсон дараалал"
                            {...form.getInputProps('featuredOrder')}
                        />

                        <Select
                            clearable
                            label='Фонограм'
                            data={phonograms}
                            {...form.getInputProps('phonograms')}
                        />

                        <Switch
                            label="Клип эсэх"
                            {...form.getInputProps('isClip', { type: 'checkbox' })}
                        />

                        <Select
                            clearable
                            withAsterisk
                            label='Гэрээ'
                            data={contractItems}
                            {...form.getInputProps('contract')}
                        />

                        <Select
                            clearable
                            withAsterisk
                            label='Зөвшөөрөл үг'
                            data={licenseItems}
                            {...form.getInputProps('licenseLyric')}
                        />

                        <Select
                            clearable
                            withAsterisk
                            label='Зөвшөөрөл ая'
                            data={licenseItems}
                            {...form.getInputProps('licenseSound')}
                        />

                        <Select
                            clearable
                            withAsterisk
                            label='Зөвшөөрөл бичлэг'
                            data={licenseItems}
                            {...form.getInputProps('licenseVideo')}
                        />

                        <Select
                            clearable
                            withAsterisk
                            label='Зөвшөөрөл дуучин'
                            data={licenseItems}
                            {...form.getInputProps('licenseSinger')}
                        />

                        <Switch
                            label="Идэвхтэй эсэх"
                            {...form.getInputProps('isActive', { type: 'checkbox' })}
                        />
                    </form>
                </Box>
            </Page.Body>
            <Page.Footer>
                <Group position="right" mt="md">
                    <Button color='color-secondary' onClick={() => form.onSubmit(handleSubmit)()}>Хадгалах</Button>
                </Group>
            </Page.Footer>
        </Page>
    );
};

export default EditSong;